import React from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import { ITaskProgress } from './taskprogress.models';
import { fetchTaskTimeline } from './taskprogress.apis';

type TimelineEvent = {
  rid?: number;
  tpi?: number;
  evt?: string;
  evd?: string;
  usi?: number;
  usn?: string;
  ttl?: string;
  dsc?: string;
  exi?: any;
  sts?: number;
};

type Props = {
  task?: ITaskProgress;
};

type State = {
  loading: boolean;
  error: string;
  rows: TimelineEvent[];
};

export default class TaskTimeline extends React.PureComponent<Props, State> {
  state: State = {
    loading: false,
    error: '',
    rows: [],
  };

  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.task?.rid !== this.props.task?.rid) {
      this.load();
    }
  }

  load = async () => {
    if (!this.props.task?.rid) {
      this.setState({ rows: [], loading: false, error: '' });
      return;
    }

    this.setState({ loading: true, error: '' });

    try {
      const rows = await fetchTaskTimeline(this.props.task.rid);
      this.setState({ rows, loading: false });
    } catch (e: any) {
      this.setState({
        loading: false,
        error: e?.message || 'Unable to load task timeline.',
      });
    }
  };

  icon = (evt?: string) => {
    switch ((evt || '').toUpperCase()) {
      case 'PROGRESS': return '📝';
      case 'EVIDENCE': return '📎';
      case 'APPROVAL': return '✅';
      case 'ACTION': return '⚡';
      default: return '•';
    }
  };

  formatDate = (value?: string) => {
    if (!value) return '';
    const d = new Date(value);
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  formatTime = (value?: string) => {
    if (!value) return '';
    const d = new Date(value);
    return d.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  groupedRows = () => {
    const groups: { [key: string]: TimelineEvent[] } = {};

    this.state.rows.forEach((r) => {
      const key = this.formatDate(r.evd) || 'Unknown date';
      groups[key] = groups[key] || [];
      groups[key].push(r);
    });

    return groups;
  };

  render() {
    const { task } = this.props;
    const { loading, error, rows } = this.state;

    if (!task?.rid) {
      return (
        <Paper elevation={0}>
          <Typography color="text.secondary">
            Save progress before timeline events are available.
          </Typography>
        </Paper>
      );
    }

    const groups = this.groupedRows();

    return (
      <Stack spacing={2}>
        {loading && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Loading timeline...
            </Typography>
          </Stack>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {!loading && rows.length === 0 && (
          <Typography color="text.secondary">
            No timeline events yet.
          </Typography>
        )}

        {Object.keys(groups).map((dateKey) => (
          <Box key={dateKey}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {dateKey}
            </Typography>

            <Stack spacing={1.5}>
              {groups[dateKey].map((r) => (
                <Paper
                  key={`${r.evt}-${r.rid}`}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Typography variant="h6" sx={{ lineHeight: 1 }}>
                      {this.icon(r.evt)}
                    </Typography>

                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2">
                          <b>{r.ttl || r.evt}</b>
                        </Typography>
                        <Chip size="small" label={r.evt || 'EVENT'} />
                      </Stack>

                      <Typography variant="caption" color="text.secondary">
                        {this.formatTime(r.evd)} · {r.usn || 'System'}
                      </Typography>

                      {r.dsc && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            {r.dsc}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    );
  }
}